<template>
  <div class="import-files-backdrop" @keydown.esc.stop="cancel">
    <div
      ref="dialog"
      class="import-files"
      :class="{ dragging, 'is-empty': isEmpty }"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('import_files.title')"
      tabindex="-1"
      @dragenter.prevent="onDragOver"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <header class="import-files__header">
        <button type="button" class="import-files__cancel" :disabled="state.isImporting" @click="cancel">{{ $t('import_files.cancel') }}</button>
        <div class="import-files__titles">
          <h2 class="import-files__title">{{ $t('import_files.title') }}</h2>
          <div v-if="subtitleText" class="import-files__subtitle" data-test="subtitle">{{ subtitleText }}</div>
        </div>
        <button
          v-if="!isEmpty"
          type="button"
          class="import-files__add"
          data-test="add-files"
          :disabled="state.isImporting"
          @click="pickFiles"
        >
          {{ $t('import_files.add_files') }}
        </button>
      </header>

      <input ref="fileInput" type="file" multiple hidden @change="onPicked">

      <!-- Empty: the whole body is one dropzone. A drop on it bubbles to the dialog's own drop
           handling, which also takes files dropped anywhere else on the dialog. -->
      <div v-if="isEmpty" class="import-files__empty-wrap">
        <div
          class="import-files__dropzone"
          :class="{ dragging }"
          role="region"
          :aria-label="$t('import_files.drop_here')"
          data-test="dropzone"
        >
          <span class="import-files__dropzone-icon" aria-hidden="true"><i class="material-icons">upload_file</i></span>
          <p class="import-files__dropzone-text">{{ $t('import_files.drop_prompt') }}</p>
          <button type="button" class="import-files__choose" data-test="choose-files" :disabled="state.isImporting" @click="pickFiles">
            {{ $t('import_files.choose_files') }}
          </button>
        </div>
      </div>

      <div v-else class="import-files__main">
        <div class="import-files__map">
          <ImportPreviewMap :model="model" />
        </div>
        <div class="import-files__list">
          <template v-if="!single">
            <div class="import-tiles">
              <div v-for="tile in tileList" :key="tile.destination" class="import-tile" :class="{ off: tile.count === 0 }">
                <b>{{ tile.count }}</b>
                <span>{{ tileLabel(t, tile.destination, tile.count) }}</span>
              </div>
            </div>
          </template>
          <ImportFileRow
            v-for="entry in shownEntries.before"
            :key="entry.id"
            v-bind="rowProps(entry)"
            @toggle="update(withToggledOpen(state, entry.id))"
            @remove="update(withRemoved(state, entry.id))"
          >
            <ImportFileSettings v-bind="settingsProps(entry)" v-on="settingsHandlers(entry)" />
          </ImportFileRow>
          <button
            v-if="bar"
            type="button"
            class="import-look"
            :aria-expanded="bar.open"
            data-test="look-bar"
            @click="update(withLookToggled(state))"
          >
            <i class="material-icons" aria-hidden="true">chevron_right</i>
            <span class="import-look__label">{{ lookBarLabel(t, bar.lookCount, bar.errorCount) }}</span>
            <small>{{ lookBarRight(t, bar.addedCount) }}</small>
          </button>
          <ImportFileRow
            v-for="entry in shownEntries.after"
            :key="entry.id"
            v-bind="rowProps(entry)"
            @toggle="update(withToggledOpen(state, entry.id))"
            @remove="update(withRemoved(state, entry.id))"
          >
            <ImportFileSettings v-bind="settingsProps(entry)" v-on="settingsHandlers(entry)" />
          </ImportFileRow>
        </div>
      </div>

      <footer v-if="state.entries.length" class="import-files__footer">
        <ImportActionButton :label="ctaText" :enabled="ctaEnabled" :importing="state.isImporting" @import="confirm" />
      </footer>

      <div v-if="dragging && !isEmpty" class="import-files__drop" aria-hidden="true">{{ $t('import_files.drop_here') }}</div>

      <ImportFailurePrompt v-if="prompt" :copy="prompt.copy" @answer="answerPrompt" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import ImportFileRow from './ImportFileRow.vue';
import ImportFileSettings from './ImportFileSettings.vue';
import ImportActionButton from './ImportActionButton.vue';
import ImportPreviewMap from './ImportPreviewMap.vue';
import ImportFailurePrompt from './ImportFailurePrompt.vue';
import { readImportFiles, type ImportFileResult } from '../../utils/import/importReader';
import { emptyPreview } from '../../utils/import/importRules';
import type { ImportIntent } from '../../utils/import/importTypes';
import {
  crossDestination,
  cta,
  emptyScreen,
  entryError,
  errorEntries,
  findEntry,
  intentDestination,
  isLoading,
  isSingle,
  lookBar,
  lookEntries,
  meta,
  readyEntries,
  tiles,
  willImport,
  type ImportChoice,
  type ImportFileEntry,
  type ImportScreenState
} from '../../utils/import/importScreenState';
import {
  withAdded,
  withChoice,
  withFiles,
  withIncludeCourse,
  withIncludeRepeats,
  withJoinDays,
  withKeepWaypoints,
  withLookToggled,
  withRemoved,
  withToggledOpen
} from '../../utils/import/importScreenUpdates';
import { mapModel } from '../../utils/import/importMapModel';
import {
  ctaLabel,
  destinationName,
  importIcon,
  lookBarLabel,
  lookBarRight,
  metaText,
  metaTone,
  subtitle,
  tileLabel,
  typeBadge
} from '../../utils/import/importTexts';
import { importConfirmProps, importPreviewProps, IMPORT_CONFIRM_EVENT, IMPORT_PREVIEW_EVENT, trackImportEvent, type ImportEntryName } from '../../utils/import/importTelemetry';
import { importFileId } from './importFileId';
import { runImport, type ImportOutcome } from './runImport';
import { failureReporter } from './failureReporter';
import {
  failurePromptCopy,
  readFailurePlans,
  saveFailurePlans,
  type ImportFailurePlan,
  type ImportFailurePromptCopy
} from '../../utils/import/importFailureReport';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';

// The Import files dialog (docs/unified-import-spec.md → "Current design"), the web twin of
// Android's ImportListDialog: Cancel / "Import files" / "+ Add files"; the map; one file
// open, or the summary tiles, the files and the "need a look" bar; and a button that says
// exactly what will be created. The rules live in utils/import/*; this only sequences them.
const props = withDefaults(defineProps<{ intent: ImportIntent; entry: ImportEntryName; initialFiles?: File[] }>(), {
  initialFiles: () => []
});
const emit = defineEmits<{ (e: 'close', outcome: ImportOutcome | null): void }>();

const { t } = useI18n();
const pref = MeasurementPrefUtility.preference();
const formatDistance = (m: number): string => pref.bigUnitFormatter(m / 1000);

const state = shallowRef<ImportScreenState>(emptyScreen(props.intent));
const update = (next: ImportScreenState): void => {
  state.value = next;
};
const dialog = ref<HTMLElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const dragging = ref(false);
let dragDepth = 0;
let closed = false;

const isEmpty = computed(() => state.value.entries.length === 0);
const single = computed(() => isSingle(state.value));
const home = computed(() => intentDestination(state.value.intent));
const subtitleText = computed(() => (state.value.entries.length ? subtitle(t, home.value, state.value.entries.length) : ''));
const model = computed(() => mapModel(state.value));
const tileList = computed(() => tiles(state.value));
const bar = computed(() => (single.value ? null : lookBar(state.value)));
const currentCta = computed(() => cta(state.value));
const ctaText = computed(() => ctaLabel(t, currentCta.value));
const ctaEnabled = computed(() => currentCta.value.kind !== 'nothing' && !state.value.entries.some(isLoading));

/** One file: just it. Several: ready files, the bar, then (when open) what it folds away. */
const shownEntries = computed(() => {
  const s = state.value;
  if (isSingle(s)) return { before: s.entries, after: [] as ImportFileEntry[] };
  const b = lookBar(s);
  return { before: readyEntries(s), after: b?.open ? [...lookEntries(s), ...errorEntries(s)] : [] };
});

function rowProps(entry: ImportFileEntry) {
  const s = state.value;
  const m = meta(s, entry);
  const dest = crossDestination(s, entry);
  return {
    name: entry.name,
    meta: metaText(t, m, formatDistance),
    tone: metaTone(m),
    typeBadge: typeBadge(t, m),
    destinationTag: dest ? t('import_files.dest_tag', { destination: destinationName(t, dest) }) : null,
    icon: importIcon(entry.preview ? entry.settings.choice : null, entryError(entry) !== null),
    expanded: s.openId === entry.id,
    loading: isLoading(entry),
    enabled: !s.isImporting
  };
}

const settingsProps = (entry: ImportFileEntry) => ({ state: state.value, entry });

function settingsHandlers(entry: ImportFileEntry) {
  const id = entry.id;
  return {
    choose: (c: ImportChoice) => update(withChoice(state.value, id, c)),
    joinDays: (v: boolean) => update(withJoinDays(state.value, id, v)),
    includeCourse: (v: boolean) => update(withIncludeCourse(state.value, id, v)),
    keepWaypoints: (v: boolean) => update(withKeepWaypoints(state.value, id, v)),
    includeRepeats: (v: boolean) => update(withIncludeRepeats(state.value, id, v)),
    remove: () => update(withRemoved(state.value, id))
  };
}

// ---- adding and reading files ------------------------------------------------------------

/** At most this many files are read at once, so a big batch doesn't hold every file in memory. */
const MAX_CONCURRENT_READS = 3;

// The files the user added, by id, so one that fails to read or save can be reported
// (importFailureReport.ts). Handles, not bytes: a file is only read again if it's sent.
const addedFiles = new Map<string, File>();
const reporter = failureReporter(id => addedFiles.get(id) ?? null, askToSend);

// "Send us this file?" — one question at a time: a batch that fails while another is being
// asked about waits its turn. Closing the dialog answers what's left with "Don't send".
const prompt = shallowRef<{ copy: ImportFailurePromptCopy; resolve: (send: boolean) => void } | null>(null);
let promptQueue: Promise<unknown> = Promise.resolve();

function askToSend(fileNames: string[]): Promise<boolean> {
  const answer = promptQueue.then(() =>
    closed ? false : new Promise<boolean>(resolve => { prompt.value = { copy: failurePromptCopy(t, fileNames), resolve }; })
  );
  promptQueue = answer;
  return answer;
}

function answerPrompt(send: boolean): void {
  const asked = prompt.value;
  prompt.value = null;
  asked?.resolve(send);
}

async function readOne(file: File): Promise<ImportFileResult[]> {
  try {
    return await readImportFiles(new Uint8Array(await file.arrayBuffer()), file.name);
  } catch {
    return [{ name: file.name, preview: emptyPreview(null, 'not_readable') }];
  }
}

/**
 * Queues newly added files (one already on the screen is ignored) and reads them; each row
 * shows its result as soon as its own file is read. Once the whole batch is read, one
 * `import_preview` event describes it.
 */
async function addFiles(files: File[]): Promise<void> {
  const known = new Set(state.value.entries.map(e => e.source));
  const fresh = files.filter((f, i) => !known.has(importFileId(f)) && files.findIndex(g => importFileId(g) === importFileId(f)) === i);
  if (!fresh.length) return;
  update(withAdded(state.value, fresh.map(f => ({ id: importFileId(f), name: f.name }))));
  const queue = [...fresh];
  const failed: ImportFailurePlan[] = [];
  const worker = async (): Promise<void> => {
    for (let file = queue.shift(); file; file = queue.shift()) {
      const results = await readOne(file);
      if (closed) return;
      const id = importFileId(file);
      addedFiles.set(id, file);
      update(withFiles(state.value, id, results));
      failed.push(...readFailurePlans(state.value.entries, id, file.name));
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_READS, fresh.length) }, worker));
  if (closed) return;
  // One question for the whole batch, once every file in it is read.
  reporter.report(failed);
  const sources = new Set(fresh.map(importFileId));
  const batch = state.value.entries.filter(e => sources.has(e.source) && e.preview);
  if (batch.length) trackImportEvent(IMPORT_PREVIEW_EVENT, importPreviewProps(props.entry, state.value, batch));
}

function pickFiles(): void {
  if (state.value.isImporting) return;
  fileInput.value?.click();
}

function onPicked(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = '';
  addFiles(files);
}

const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes('Files');

function onDragOver(e: DragEvent): void {
  if (!hasFiles(e) || state.value.isImporting) return;
  if (e.type === 'dragenter') dragDepth++;
  dragging.value = true;
}

function onDragLeave(): void {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) dragging.value = false;
}

function onDrop(e: DragEvent): void {
  dragDepth = 0;
  dragging.value = false;
  if (state.value.isImporting) return;
  addFiles(Array.from(e.dataTransfer?.files ?? []));
}

// ---- the button ------------------------------------------------------------------------

async function confirm(): Promise<void> {
  const s = state.value;
  if (s.isImporting || !s.entries.some(willImport)) return;
  trackImportEvent(IMPORT_CONFIRM_EVENT, importConfirmProps(props.entry, s));
  update({ ...s, isImporting: true });
  const outcome = await runImport(s, t);
  if (closed) return;
  // The dialog stays up until the user answers; the reports go on after it closes.
  await reporter.report(saveFailurePlans(outcome.failures, s.entries, source => addedFiles.get(source)?.name ?? null));
  close(outcome);
}

function cancel(): void {
  if (state.value.isImporting) return;
  close(null);
}

function close(outcome: ImportOutcome | null): void {
  if (closed) return;
  closed = true;
  emit('close', outcome);
}

onMounted(async () => {
  await nextTick();
  dialog.value?.focus();
  if (props.initialFiles.length) addFiles(props.initialFiles);
});

onBeforeUnmount(() => {
  closed = true;
  answerPrompt(false);
});

defineExpose({ addFiles, state, findEntry });
</script>

<style scoped>
.import-files-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(9, 10, 13, 0.62);
  display: grid;
  place-items: center;
  padding: 20px;
}
.import-files {
  position: relative;
  width: min(960px, 100%);
  height: min(680px, calc(100vh - 40px));
  background: var(--dark);
  color: var(--white);
  border-radius: 14px;
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.9);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  outline: none;
}
/* Empty, the dialog is just a dropzone, so it starts compact and grows once files are added. */
.import-files.is-empty {
  width: min(520px, 100%);
  height: min(440px, calc(100vh - 40px));
}
.import-files.dragging:not(.is-empty) {
  box-shadow: 0 0 0 2px var(--yellow), 0 30px 80px -20px rgba(0, 0, 0, 0.9);
}
.import-files__header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 10px;
}
.import-files__cancel,
.import-files__add {
  background: none;
  border: 0;
  padding: 4px 0;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.import-files__cancel {
  justify-self: start;
  color: var(--white);
}
.import-files__add {
  justify-self: end;
  color: var(--yellow);
  white-space: nowrap;
}
.import-files__cancel:disabled,
.import-files__add:disabled {
  opacity: 0.5;
  cursor: default;
}
.import-files__titles {
  text-align: center;
  min-width: 0;
}
.import-files__title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
}
.import-files__subtitle {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.import-files__empty-wrap {
  display: flex;
  min-height: 0;
  padding: 4px 20px 20px;
}
.import-files__dropzone {
  flex: 1;
  min-height: 0;
  border: 2px dashed #505259;
  border-radius: 12px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  transition: border-color 0.15s, background-color 0.15s;
}
.import-files__dropzone.dragging {
  border-color: var(--yellow);
  background: rgba(253, 223, 0, 0.06);
}
.import-files__dropzone-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gray);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.import-files__dropzone-icon .material-icons {
  font-size: 30px;
  color: #acb0bf;
}
.import-files__dropzone-text {
  margin: 0;
  max-width: 360px;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.45;
}
.import-files__choose {
  height: 44px;
  padding: 0 28px;
  border-radius: 999px;
  border: 0;
  background: var(--yellow);
  color: #000;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
}
.import-files__choose:disabled {
  opacity: 0.5;
  cursor: default;
}
.import-files__main {
  display: grid;
  grid-template-columns: minmax(0, 440px) minmax(0, 1fr);
  /* A bounded row, so the list scrolls inside the dialog instead of running off its edge. */
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
}
.import-files__map {
  grid-column: 2;
  grid-row: 1;
  padding: 4px 18px 14px 4px;
  min-height: 0;
}
.import-files__list {
  grid-column: 1;
  grid-row: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 14px 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
/* Cards keep their height and the list scrolls; shrinking them to fit clips their text. */
.import-files__list > * {
  flex-shrink: 0;
}
.import-tiles {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 8px;
}
.import-tile {
  border-radius: 12px;
  background: var(--gray);
  border: 1px solid var(--gray-border);
  padding: 10px 6px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.import-tile b {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.import-tile span {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.import-tile.off {
  opacity: 0.45;
}
.import-look {
  position: sticky;
  top: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--gray-border);
  background: var(--gray);
  box-shadow: 0 0 0 6px var(--dark);
  color: var(--orange);
  font-weight: 700;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.import-look .material-icons {
  font-size: 20px;
  transition: transform 0.15s;
}
.import-look[aria-expanded='true'] .material-icons {
  transform: rotate(90deg);
}
.import-look__label {
  flex: 1;
}
.import-look small {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
}
.import-files__footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px 16px;
  border-top: 1px solid var(--gray-raised);
}
.import-files__drop {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(23, 24, 29, 0.85);
  color: var(--yellow);
  font-size: 18px;
  font-weight: 800;
  pointer-events: none;
}

/* Mobile widths: full screen, the map on top and the list under it. */
@media (max-width: 767.98px) {
  .import-files-backdrop {
    padding: 0;
  }
  .import-files,
  .import-files.is-empty {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
  .import-files__empty-wrap {
    padding: 4px 16px calc(16px + env(safe-area-inset-bottom));
  }
  .import-files__header {
    padding: 12px 16px 8px;
  }
  .import-files__main {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 180px minmax(0, 1fr);
  }
  .import-files__map {
    grid-column: 1;
    grid-row: 1;
    padding: 4px 16px 8px;
  }
  .import-files__list {
    grid-column: 1;
    grid-row: 2;
    padding: 4px 16px 12px;
  }
  .import-files__footer {
    padding: 10px 16px calc(14px + env(safe-area-inset-bottom));
  }
  .import-files__footer :deep(.import-action) {
    width: 100%;
  }
}
</style>
